/**
 * Summary Cards Container Component
 * Displays 4 stat cards in a responsive grid with API data
 */

import { Skeleton } from "@nextui-org/react";
import { BellDot, Building2, ClipboardList, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCallback } from "react";
import { ROUTES } from "@/shared/constants/routes";
import { useGetHomeSummary } from "../requests";
import { SectionError } from "./SectionError";
import { type CardAccentColor, SummaryCard } from "./SummaryCard";

/**
 * Determines the accent color based on risk score
 * - Red: score < 50 (high risk)
 * - Yellow: score 50-69 (medium risk)
 * - Green: score >= 70 (low risk)
 */
const getRiskScoreColor = (score: number): CardAccentColor => {
  if (score < 50) return "red";
  if (score < 70) return "yellow";
  return "green";
};

/**
 * Loading skeleton for summary cards
 */
const SummaryCardsSkeleton = () => (
  <div className="flex flex-row items-center gap-4">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="flex-1">
        <div className="flex flex-col border-t-2 border-gray-200 rounded-lg shadow-sm">
          <div className="flex flex-col justify-between p-4 gap-4 bg-white rounded-lg h-[148px]">
            <div className="flex flex-row items-start">
              <div className="flex flex-col gap-2 flex-1">
                <Skeleton className="h-4 w-24 rounded" />
                <Skeleton className="h-7 w-16 rounded" />
              </div>
              <Skeleton className="w-12 h-12 rounded-lg" />
            </div>
            <div className="flex flex-col gap-1">
              <Skeleton className="h-4 w-20 rounded" />
              <Skeleton className="h-4 w-16 rounded" />
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

interface SummaryCardsProps {
  onScrollToTasks?: () => void;
  onScrollToEvents?: () => void;
}

export const SummaryCards = ({ onScrollToTasks, onScrollToEvents }: SummaryCardsProps) => {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useGetHomeSummary();

  const handleTotalAssessmentsClick = useCallback(() => {
    navigate(`/${ROUTES.THIRD_PARTY_VENDORS_V2}`);
  }, [navigate]);

  // Loading state
  if (isLoading) {
    return <SummaryCardsSkeleton />;
  }

  // Error state - single full-width container with centered error
  if (isError || !data) {
    return (
      <div className="flex flex-col border-t-2 border-gray-200 rounded-lg shadow-sm">
        <div className="flex items-center justify-center p-4 bg-white rounded-lg h-[148px]">
          <SectionError title="Failed to load summary" onRetry={() => refetch()} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-row items-center gap-4">
      {/* Card 1: Total Third-Parties - CLICKABLE */}
      <div className="flex-1">
        <SummaryCard
          title="Total Assessments"
          value={data.total_assessment.count}
          icon={Building2}
          accentColor="blue"
          secondaryInfo={{
            label: "New Assessments",
            value: `+${data.total_assessment.new_this_month} this month`,
            showTrend: data.total_assessment.new_this_month > 0,
            trendDirection: "up",
          }}
          onClick={handleTotalAssessmentsClick}
          isClickable={true}
        />
      </div>

      {/* Card 2: Risk Posture Score - NOT CLICKABLE */}
      <div className="flex-1">
        <SummaryCard
          title="Risk Posture Score (RPS)"
          value={data.overall_risk.score}
          secondaryValue={`/${data.overall_risk.max}`}
          icon={Shield}
          accentColor={getRiskScoreColor(data.overall_risk.score)}
          tooltip={data.overall_risk.formula}
          isClickable={false}
        />
      </div>

      {/* Card 3: Monitoring Alerts - CLICKABLE (scroll) */}
      <div className="flex-1">
        <SummaryCard
          title="Monitoring Alerts"
          value={data.monitoring_alerts.total}
          icon={BellDot}
          accentColor="red"
          secondaryInfo={[
            {
              label: "Breaches",
              value: `+${data.monitoring_alerts.breach_events.new_today} today`,
              showTrend: data.monitoring_alerts.breach_events.new_today > 0,
              trendDirection: "up",
            },
            {
              label: "SOC2 Expired",
              value: `+${data.monitoring_alerts.soc2_expired.new_today} today`,
              showTrend: data.monitoring_alerts.soc2_expired.new_today > 0,
              trendDirection: "up",
            },
          ]}
          onClick={onScrollToEvents}
          isClickable={true}
        />
      </div>

      {/* Card 4: Pending Tasks - CLICKABLE (scroll) */}
      <div className="flex-1">
        <SummaryCard
          title="Pending Tasks"
          value={data.pending_tasks.total}
          icon={ClipboardList}
          accentColor="yellow"
          secondaryInfo={{
            label: "Awaiting action",
            value: `${data.pending_tasks.awaiting_action.due_this_week} due this week`,
            showTrend: false,
          }}
          onClick={onScrollToTasks}
          isClickable={true}
        />
      </div>
    </div>
  );
};

export default SummaryCards;
